import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { TradingCall, TradingCallConfig, Wallet } from '../types/TradingCall';
import type { TradingCall as ReportTradingCall } from '../types/report';
import {
  mapReportTradingCallToWalletCall,
  mapOllamaStrategyToWalletCall,
  type OllamaStrategyInput,
} from '../utils/portfolioCallMappers';
import { TradingCallEngine } from '../services/tradingCallEngine';
import { SimpleEnhancedTradingCallEngine } from '../services/simpleEnhancedTradingCallEngine';
import { WalletService } from '../services/walletService';
import { logger } from '../utils/logger';
import { ScannerData } from '../types/ScannerData';
import { addCallTrackerAlert, addTradingCallAlert } from '../utils/alertRaise';
import { getFilterOptions } from '../utils/filterUtils';
import { estimateMarkPrice } from '../utils/virtualPortfolioMtm';
import { getApiUrl } from '../config/api';
import { getAuthToken } from '../utils/authUtils';
import { mapCompanionServerCallToWalletCall } from '../utils/companionSync';

const PAPER_WALLET_STORAGE_KEY = 'mmec_paper_wallet_v1';

function mapCallStatusToRecommendationOutcome(status: TradingCall['status']): string {
  switch (status) {
    case 'TARGET_HIT':
      return 'target_hit';
    case 'STOP_LOSS':
      return 'stop_loss';
    case 'EXPIRED':
      return 'expired';
    case 'MANUAL_CLOSE':
      return 'manual_close';
    case 'CANCELLED':
      return 'cancelled';
    default:
      return 'manual_close';
  }
}

/** Realized P&amp;L safe for JSON (NaN becomes null — JSON.stringify drops NaN otherwise). */
function safeRealizedPnl(pnl: number | undefined): number | null {
  if (pnl == null) return null;
  if (!Number.isFinite(pnl)) return null;
  return pnl;
}

/** R-multiple vs initial debit/credit magnitude (paper trade). */
function rMultipleForPaperCall(call: TradingCall, pnl: number | null): number | null {
  if (pnl == null) return null;
  const risk = Math.abs(call.entryPrice * call.quantity);
  if (!(risk > 0) || !Number.isFinite(risk)) return null;
  const r = pnl / risk;
  return Number.isFinite(r) ? Math.round(r * 1000) / 1000 : null;
}

/** Sync paper close to persisted strategy row so the next generator run sees real outcomes. */
function syncRecommendationOutcome(
  call: TradingCall | undefined,
  status: TradingCall['status'],
  pnl: number | undefined,
): void {
  const rid = call?.strategyRecommendationId;
  if (!rid) return;
  const token = getAuthToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const safePnl = safeRealizedPnl(pnl);
  const rMult = call && safePnl != null ? rMultipleForPaperCall(call, safePnl) : null;
  fetch(getApiUrl(`/api/ai/strategy-recommendations/${encodeURIComponent(rid)}/outcome`), {
    method: 'POST',
    headers,
    credentials: 'same-origin',
    body: JSON.stringify({
      outcome: mapCallStatusToRecommendationOutcome(status),
      pnl: safePnl,
      ...(rMult != null ? { r_multiple: rMult } : {}),
    }),
  }).catch(() => {});
}

interface TradingCallContextType {
  // Configuration
  config: TradingCallConfig;
  updateConfig: (newConfig: Partial<TradingCallConfig>) => void;
  
  // Trading calls
  activeCalls: TradingCall[];
  closedCalls: TradingCall[];
  generateCalls: (data: ScannerData[]) => void;
  addCall: (call: TradingCall) => void;
  /** Map a report-row trading call into the paper wallet (elite_pulse layer). Skips if already active with same id. */
  addCallFromElitePulse: (reportCall: ReportTradingCall) => void;
  /** Add generator / model strategy rows as platform + ollama positions. Pass recommendation ids from the last generate response (aligned by index) to link paper P&L to stored rows. */
  addOllamaStrategiesToPortfolio: (strategies: OllamaStrategyInput[], recommendationIds?: (string | undefined)[]) => void;
  closeCall: (callId: string, status: TradingCall['status'], closePrice: number) => void;
  manualCloseCall: (callId: string, closePrice: number) => void;
  cancelCall: (callId: string) => void;
  
  // Wallet
  wallet: Wallet;
  updateCallPerformance: (callId: string, currentPrice: number) => void;
  
  // Performance tracking — pass scanner rows to mark open positions to market
  refreshAllCallPerformance: (scannerData?: ScannerData[]) => void;
  /** Paper starting capital (does not clear positions) */
  setVirtualStartingCapital: (amount: number) => void;

  // Data management
  exportWalletData: () => string;
  importWalletData: (data: string) => void;
  /** Clear simulated positions and balance back to defaults (browser only) */
  resetPaperWallet: () => void;
}

const TradingCallContext = createContext<TradingCallContextType | undefined>(undefined);

export const useTradingCall = () => {
  const context = useContext(TradingCallContext);
  if (!context) {
    throw new Error('useTradingCall must be used within a TradingCallProvider');
  }
  return context;
};

interface TradingCallProviderProps {
  children: React.ReactNode;
}

export const TradingCallProvider: React.FC<TradingCallProviderProps> = ({ children }) => {
  // Use a ref to store alert functions to avoid conditional hook calls
  // Trading-call alerts are events, so they are raised directly rather than matched by a rule.
  
  // Initialize default configuration
  const [config, setConfig] = useState<TradingCallConfig>({
    enabled: true, // On: App feeds scanner rows into generateCalls; duplicate batches skipped via hash
    preferredTickers: ['SPY', 'QQQ', 'GLD', '^NDX', '^GSPC', 'GC=F', 'AAPL', 'MSFT', 'TSLA', 'NVDA', 'AMD', 'META', 'GOOGL', 'AMZN', 'NFLX', 'CRM', 'ADBE', 'PYPL', 'INTC', 'ORCL'],
    callTypes: {
      futures: true,
      options: true
    },
    strategies: {
      nakedCalls: true,
      nakedPuts: true,
      verticalSpreads: true,
      complexSpreads: true,
      straddles: true,
      strangles: true,
      collars: true,
      ironCondors: true
    },
    riskManagement: {
      maxPositionSize: 100,
      maxRiskPerTrade: 500,
      defaultStopLoss: 0.3,
      defaultTarget: 0.5,
      maxCallsPerUnderlying: 1, // Only one call per underlying per type
      aggressiveTrading: false // Disabled by default
    },
    sentimentThresholds: {
      bullish: 0.6,
      bearish: 0.4,
      confidence: 50, // Higher threshold for better quality calls
      minProfitPotential: 0.2, // Minimum 20% profit potential
      maxRiskRewardRatio: 3.0 // Maximum 1:3 risk/reward ratio
    },
    filters: {
      minIV: 0.15, // Minimum 15% IV
      maxIV: 0.80, // Maximum 80% IV
      minVolume: 50, // Minimum volume
      minDelta: 0.1, // Minimum delta
      maxDelta: 0.9, // Maximum delta
      minDaysToExpiry: 7, // Minimum 7 days to expiry
      maxDaysToExpiry: 45 // Maximum 45 days to expiry
    }
  });

  // Initialize services
  const [engine] = useState(() => new SimpleEnhancedTradingCallEngine(config));
  const [walletService] = useState(() => new WalletService(100000));
  
  // State
  const [activeCalls, setActiveCalls] = useState<TradingCall[]>([]);
  const [closedCalls, setClosedCalls] = useState<TradingCall[]>([]);
  const [wallet, setWallet] = useState<Wallet>(walletService.getWallet());
  
  // Track last processed data to prevent duplicate calls
  const lastProcessedDataHash = useRef<string>('');
  /** Last scanner snapshot — used so MTM can run on interval without App passing rows every time */
  const lastScannerRowsRef = useRef<ScannerData[]>([]);

  const [paperHydrated, setPaperHydrated] = useState(false);

  // Update engine when config changes
  useEffect(() => {
    engine['config'] = config;
  }, [config, engine]);

  // Update configuration
  const updateConfig = useCallback((newConfig: Partial<TradingCallConfig>) => {
    setConfig(prev => ({ ...prev, ...newConfig }));
  }, [setConfig]);

  // Aggressive trading: Close existing calls and open new ones when direction changes
  const handleAggressiveTrading = useCallback((data: ScannerData[]) => {
    if (!config.riskManagement.aggressiveTrading) return;

    // Group data by underlying
    const groupedData: Record<string, ScannerData[]> = {};
    data.forEach(item => {
      const underlying = item.Underlying || item.underlying_symbol || 'Unknown';
      if (!groupedData[underlying]) {
        groupedData[underlying] = [];
      }
      groupedData[underlying].push(item);
    });
    
    for (const underlying of Object.keys(groupedData)) {
      const optionsData = groupedData[underlying];
      const existingCalls = engine.getActiveCallsByUnderlying(underlying);
      if (existingCalls.length === 0) continue;

      // Analyze current sentiment
      const bullishCount = optionsData.filter(item => 
        item['proper_sentiment'] === 'BULLISH' || item['proper_sentiment'] === 'Strong Bullish'
      ).length;
      const bearishCount = optionsData.filter(item => 
        item['proper_sentiment'] === 'BEARISH' || item['proper_sentiment'] === 'Strong Bearish'
      ).length;
      const totalCount = optionsData.length;
      
      let currentSentiment = 'NEUTRAL';
      if (totalCount > 0) {
        const bullishRatio = bullishCount / totalCount;
        if (bullishRatio > 0.6) currentSentiment = 'BULLISH';
        else if (bullishRatio < 0.4) currentSentiment = 'BEARISH';
      }
      
      existingCalls.forEach(call => {
        const callDirection = call.sentiment.includes('BULLISH') ? 'BULLISH' : 'BEARISH';
        const currentDirection = currentSentiment.includes('BULLISH') ? 'BULLISH' : 'BEARISH';
        
        // If direction has changed, close the call
        if (callDirection !== currentDirection) {
          logger.debug(`Aggressive trading: Closing ${call.symbol} due to direction change from ${callDirection} to ${currentDirection}`);
          // Use walletService directly to avoid dependency issues
          walletService.closeCall(call.id, 'MANUAL_CLOSE', call.entryPrice);
        }
      });
    }
  }, [config.riskManagement.aggressiveTrading, engine]);

  // Generate trading calls from market data
  const generateCalls = useCallback((data: ScannerData[]) => {
    if (!config.enabled) {
      logger.debug('Trading call generation disabled');
      return;
    }

    try {
      // Create a unique hash for the current data to prevent duplicate processing
      const dataHash = JSON.stringify(data.map(item => ({
        Symbol: item.Symbol,
        'Expiry Date': item['Expiry Date'],
        'Bid Volume': item['Bid Volume'],
        'Ask Volume': item['Ask Volume'],
        'Sentiment': item['Sentiment'],
        'Volume': item['Volume'],
        'Delta': item['Delta'] || item['delta']
      })));

      // Check if we've already processed this exact data
      if (lastProcessedDataHash.current === dataHash) {
        logger.debug('Trading call data already processed, skipping generation');
        return;
      }

      logger.debug(`Generating trading calls from ${data.length} data points`);
      logger.debug('Preferred tickers:', config.preferredTickers);
      logger.debug('Confidence threshold:', config.sentimentThresholds.confidence);
      
      // Log unique underlying symbols in data
      const uniqueSymbols = getFilterOptions(data).availableUnderlyings;
      logger.debug('Unique symbols in data:', uniqueSymbols);
      
      // Handle aggressive trading first
      handleAggressiveTrading(data);
      
      const newCalls = engine.generateTradingCalls(data);
      
      logger.debug(`Generated ${newCalls.length} new trading calls`);
      
      // Add new calls to wallet and trigger alerts
      newCalls.forEach(call => {
        walletService.addTradingCall(call);
        // Trigger alert for new trading call
        addTradingCallAlert('new', call);
      });

      // Update state
      setActiveCalls(walletService.getActiveCalls());
      setClosedCalls(walletService.getClosedCalls());
      setWallet(walletService.getWallet());
      
      // Update the hash to mark this data as processed
      lastProcessedDataHash.current = dataHash;
      
      logger.debug(`Total active calls: ${walletService.getActiveCalls().length}`);
      logger.debug(`Total closed calls: ${walletService.getClosedCalls().length}`);
    } catch (error) {
      logger.error('Error generating trading calls:', error);
    }
  }, [config.enabled, config.preferredTickers, config.sentimentThresholds.confidence, handleAggressiveTrading, engine, walletService]);

  // Add a call manually
  const addCall = useCallback((call: TradingCall) => {
    walletService.addTradingCall(call);
    setActiveCalls(walletService.getActiveCalls());
    setClosedCalls(walletService.getClosedCalls());
    setWallet(walletService.getWallet());
    // Trigger alert for new trading call
    addTradingCallAlert('new', call);
  }, [walletService, addTradingCallAlert]);

  const addCallFromElitePulse = useCallback(
    (reportCall: ReportTradingCall) => {
      const walletCallId = `ep_${reportCall.id}`;
      if (walletService.getActiveCalls().some((c) => c.id === walletCallId)) {
        return;
      }
      const mapped = mapReportTradingCallToWalletCall(reportCall);
      walletService.addTradingCall(mapped);
      setActiveCalls(walletService.getActiveCalls());
      setClosedCalls(walletService.getClosedCalls());
      setWallet(walletService.getWallet());
      addTradingCallAlert('new', mapped);
    },
    [walletService, addTradingCallAlert],
  );

  const addOllamaStrategiesToPortfolio = useCallback(
    (strategies: OllamaStrategyInput[], recommendationIds?: (string | undefined)[]) => {
      if (!strategies.length) return;
      strategies.forEach((s, i) => {
        const rid = recommendationIds?.[i];
        const mapped = mapOllamaStrategyToWalletCall(s, i, rid);
        walletService.addTradingCall(mapped);
        addTradingCallAlert('new', mapped);
      });
      setActiveCalls(walletService.getActiveCalls());
      setClosedCalls(walletService.getClosedCalls());
      setWallet(walletService.getWallet());
    },
    [walletService, addTradingCallAlert],
  );

  // Close a call
  const closeCall = useCallback((callId: string, status: TradingCall['status'], closePrice: number) => {
    const call = walletService.getActiveCalls().find(c => c.id === callId);
    walletService.closeCall(callId, status, closePrice);
    setActiveCalls(walletService.getActiveCalls());
    setClosedCalls(walletService.getClosedCalls());
    setWallet(walletService.getWallet());
    
    // Remove from engine tracking
    if (call) {
      engine.removeCall(callId);
    }
    
    // Trigger alert for call closure (disabled by default)
    const closedCall = walletService.getClosedCalls().find(c => c.id === callId);
    if (call) {
      syncRecommendationOutcome(call, status, closedCall?.performance?.pnl);
    }
    if (call && closedCall && closedCall.performance) {
      // Enable call tracker alerts
      if (status === 'TARGET_HIT') {
        addCallTrackerAlert('profit', { ...call, profit: closedCall.performance.pnl });
      } else if (status === 'STOP_LOSS') {
        addCallTrackerAlert('loss', { ...call, loss: Math.abs(closedCall.performance.pnl || 0) });
      } else {
        addCallTrackerAlert('did_not_work', { ...call, reason: status });
      }
    }
  }, [walletService, engine, addCallTrackerAlert]);

  // Manually close a call
  const manualCloseCall = useCallback((callId: string, closePrice: number) => {
    const call = walletService.getActiveCalls().find(c => c.id === callId);
    walletService.manualCloseCall(callId, closePrice);
    setActiveCalls(walletService.getActiveCalls());
    setClosedCalls(walletService.getClosedCalls());
    setWallet(walletService.getWallet());
    const closedCall = walletService.getClosedCalls().find(c => c.id === callId);
    syncRecommendationOutcome(call, 'MANUAL_CLOSE', closedCall?.performance?.pnl);
  }, [walletService]);

  // Cancel a call
  const cancelCall = useCallback((callId: string) => {
    const call = walletService.getActiveCalls().find(c => c.id === callId);
    walletService.cancelCall(callId);
    setActiveCalls(walletService.getActiveCalls());
    setClosedCalls(walletService.getClosedCalls());
    setWallet(walletService.getWallet());
    const closedCall = walletService.getClosedCalls().find(c => c.id === callId);
    syncRecommendationOutcome(call, 'CANCELLED', closedCall?.performance?.pnl);

    // Remove from engine tracking
    engine.removeCall(callId);
  }, [walletService, engine]);

  // Update call performance
  const updateCallPerformance = useCallback((callId: string, currentPrice: number) => {
    walletService.updateCallPerformance(callId, currentPrice);
    setActiveCalls(walletService.getActiveCalls());
    setClosedCalls(walletService.getClosedCalls());
    setWallet(walletService.getWallet());
  }, [walletService]);

  const refreshAllCallPerformance = useCallback(
    (scannerData?: ScannerData[]) => {
      if (scannerData?.length) {
        lastScannerRowsRef.current = scannerData;
      }
      const rows = scannerData?.length ? scannerData : lastScannerRowsRef.current;
      if (rows?.length) {
        const ids = walletService.getActiveCalls().map((c) => c.id);
        for (const id of ids) {
          const call = walletService.getCallById(id);
          if (!call || call.status !== 'ACTIVE') continue;
          const mark = estimateMarkPrice(call, rows);
          if (mark != null && mark > 0) {
            walletService.updateCallPerformance(id, mark);
          }
        }
      }
      setActiveCalls(walletService.getActiveCalls());
      setClosedCalls(walletService.getClosedCalls());
      setWallet(walletService.getWallet());
    },
    [walletService]
  );

  const setVirtualStartingCapital = useCallback(
    (amount: number) => {
      walletService.setInitialBalance(amount);
      setActiveCalls(walletService.getActiveCalls());
      setClosedCalls(walletService.getClosedCalls());
      setWallet(walletService.getWallet());
    },
    [walletService]
  );

  // Export wallet data
  const exportWalletData = useCallback(() => {
    return walletService.exportWalletData();
  }, [walletService]);

  // Import wallet data
  const importWalletData = useCallback((data: string) => {
    walletService.importWalletData(data);
    setActiveCalls(walletService.getActiveCalls());
    setClosedCalls(walletService.getClosedCalls());
    setWallet(walletService.getWallet());
  }, [walletService]);

  const resetPaperWallet = useCallback(() => {
    const cap = walletService.getWallet().initialBalance;
    const fresh = new WalletService(cap);
    walletService.importWalletData(fresh.exportWalletData());
    setActiveCalls(walletService.getActiveCalls());
    setClosedCalls(walletService.getClosedCalls());
    setWallet(walletService.getWallet());
    lastProcessedDataHash.current = '';
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(PAPER_WALLET_STORAGE_KEY);
      }
    } catch {
      /* ignore quota */
    }
  }, [walletService]);

  // Restore paper wallet from browser (session persistence)
  useEffect(() => {
    if (typeof localStorage === 'undefined') {
      setPaperHydrated(true);
      return;
    }
    try {
      const raw = localStorage.getItem(PAPER_WALLET_STORAGE_KEY);
      if (raw) {
        walletService.importWalletData(raw);
        setActiveCalls(walletService.getActiveCalls());
        setClosedCalls(walletService.getClosedCalls());
        setWallet(walletService.getWallet());
      }
    } catch (e) {
      logger.warn('Paper wallet restore failed', e);
    } finally {
      setPaperHydrated(true);
    }
  }, [walletService]);

  // Persist paper wallet (interval covers in-place mutations that skip React state identity)
  useEffect(() => {
    if (!paperHydrated || typeof localStorage === 'undefined') return;
    const tick = () => {
      try {
        localStorage.setItem(PAPER_WALLET_STORAGE_KEY, walletService.exportWalletData());
      } catch (e) {
        logger.warn('Paper wallet persist failed', e);
      }
    };
    tick();
    const id = window.setInterval(tick, 30000);  // was 2000 — reduced to avoid localStorage thrashing
    return () => window.clearInterval(id);
  }, [paperHydrated, walletService]);

  // Periodic state sync (MTM happens when App passes scanner rows after each fetch)
  useEffect(() => {
    const interval = setInterval(() => {
      refreshAllCallPerformance();
    }, 60000);

    return () => clearInterval(interval);
  }, [refreshAllCallPerformance]);

  const syncCompanionPortfolio = useCallback(async () => {
    try {
      const res = await fetch(getApiUrl('/api/companion/state'));
      if (!res.ok) return;
      const data = await res.json();
      if (!data?.success || !data.state) return;
      const serverActive = (data.state.active_calls || []) as Record<string, unknown>[];
      const serverIds = new Set(
        serverActive.map((row) => `cmp_${String(row.id || '')}`).filter((id) => id !== 'cmp_'),
      );
      let added = false;
      for (const row of serverActive) {
        const mapped = mapCompanionServerCallToWalletCall(row);
        if (walletService.getActiveCalls().some((c) => c.id === mapped.id)) continue;
        walletService.addTradingCall(mapped);
        addTradingCallAlert('new', mapped);
        added = true;
      }
      for (const c of walletService.getActiveCalls()) {
        if (!c.id.startsWith('cmp_') || serverIds.has(c.id)) continue;
        walletService.manualCloseCall(c.id, c.performance?.currentPrice ?? c.entryPrice);
      }
      if (added || serverActive.length) {
        setActiveCalls(walletService.getActiveCalls());
        setClosedCalls(walletService.getClosedCalls());
        setWallet(walletService.getWallet());
      }
    } catch (e) {
      logger.debug('Companion portfolio sync skipped', e);
    }
  }, [walletService, addTradingCallAlert]);

  useEffect(() => {
    if (!paperHydrated) return;
    void syncCompanionPortfolio();
    const id = window.setInterval(() => void syncCompanionPortfolio(), 60_000);
    return () => window.clearInterval(id);
  }, [paperHydrated, syncCompanionPortfolio]);

  useEffect(() => {
    if (!paperHydrated) return;
    const onWalletAdd = () => {
      void syncCompanionPortfolio();
    };
    window.addEventListener('mmec-companion-wallet-add', onWalletAdd);
    return () => window.removeEventListener('mmec-companion-wallet-add', onWalletAdd);
  }, [paperHydrated, syncCompanionPortfolio]);

  const value: TradingCallContextType = {
    config,
    updateConfig,
    activeCalls,
    closedCalls,
    generateCalls,
    addCall,
    addCallFromElitePulse,
    addOllamaStrategiesToPortfolio,
    closeCall,
    manualCloseCall,
    cancelCall,
    wallet,
    updateCallPerformance,
    refreshAllCallPerformance,
    setVirtualStartingCapital,
    exportWalletData,
    importWalletData,
    resetPaperWallet
  };

  return (
    <TradingCallContext.Provider value={value}>
      {children}
    </TradingCallContext.Provider>
  );
}; 